import {
  Tooltip as FluentTooltip,
  PositioningShorthand,
} from '@fluentui/react-components';
import React, { HTMLAttributes, ReactElement, ReactNode } from 'react';
import cn from 'classnames';
import { useFluentMountNode } from '../hooks';
import './Tooltip.scss';

/** Position of the tooltip relative to the trigger */
export type TooltipPosition = 'above' | 'below' | 'before' | 'after';

/** Alignment of the tooltip relative to the trigger */
export type TooltipAlign = 'start' | 'center' | 'end';

/** Relationship of the tooltip to its trigger for accessibility */
export type TooltipRelationship = 'label' | 'description' | 'inaccessible';

export interface TooltipProps extends Omit<HTMLAttributes<HTMLDivElement>, 'content'> {
  /** Element that triggers the tooltip on hover/focus */
  trigger: ReactElement;
  /** Content to display inside the tooltip */
  content: ReactNode;
  /** Position of the tooltip relative to the trigger */
  position?: TooltipPosition;
  /** Alignment of the tooltip relative to the trigger */
  align?: TooltipAlign;
  /** Whether to show a pointing arrow */
  pointing?: boolean;
  /**
   * Accessibility relationship between tooltip and trigger.
   * - 'label': Sets aria-label/aria-labelledby on trigger (default)
   * - 'description': Sets aria-description/aria-describedby on trigger
   * - 'inaccessible': No aria attributes (use only if content is available elsewhere)
   */
  relationship?: TooltipRelationship;
  /**
   * Visual appearance of the tooltip.
   * - 'normal': Uses theme background and text colors (default)
   * - 'inverted': Higher contrast variant with inverted colors
   */
  appearance?: 'normal' | 'inverted';
}

// Internal mapping: our position + align to Fluent positioning
const getFluentPositioning = (
  position: TooltipPosition,
  align: TooltipAlign,
): PositioningShorthand => {
  const alignSuffix = align === 'center' ? '' : `-${align}`;
  return `${position}${alignSuffix}` as PositioningShorthand;
};

/**
 * Tooltip component with semantic API.
 * Implementation uses Fluent UI v9 Tooltip internally but exposes a library-agnostic interface.
 *
 * @example
 * // Basic usage
 * <Tooltip
 *   trigger={<Button icon={<InfoIcon />} />}
 *   content="More information"
 * />
 *
 * @example
 * // With positioning and arrow
 * <Tooltip
 *   trigger={<span>Hover me</span>}
 *   content="Helpful tooltip text"
 *   position="below"
 *   pointing
 * />
 */
export function Tooltip({
  trigger,
  content,
  position = 'above',
  align = 'center',
  pointing = false,
  relationship = 'label',
  appearance = 'normal',
  className,
  ...rest
}: TooltipProps) {
  const positioning = getFluentPositioning(position, align);

  // Get portal mount node inside FluentProvider for proper theme support
  const mountNode = useFluentMountNode();

  const tooltipClass = cn('aix-tooltip', className);

  return (
    <FluentTooltip
      content={{
        children: content,
        className: tooltipClass,
        ...rest,
      }}
      positioning={positioning}
      relationship={relationship}
      withArrow={pointing}
      appearance={appearance}
      mountNode={mountNode}
    >
      {trigger}
    </FluentTooltip>
  );
}

Tooltip.displayName = 'Tooltip';
